import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { adminUpdateAppointmentById } from "../../../config/service";
import {
  glassDialogPaperProps,
  glassDialogBackdropProps,
} from "./consultationsGlassDialog";

/**
 * Admin comments modal used only on the Consultations route (AdminConsultations).
 * Other admin pages continue to use ./Comment.jsx.
 */
export default function ConsultationsAdminCommentsModal({
  open,
  onClose,
  meetingId,
  initialComment,
  lang = "EN",
}) {
  const { t } = useTranslation();
  const dir = lang === "HE" ? "rtl" : "ltr";
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(initialComment != null ? String(initialComment) : "");
    }
  }, [open, initialComment, meetingId]);

  const handleSave = async () => {
    if (!meetingId) return;
    setSaving(true);
    const data = {
      comments: value,
      meetingIds: [meetingId],
    };
    try {
      const response = await adminUpdateAppointmentById(data);
      if ([200, 201, 202].includes(response.status)) {
        toast.success(t(response.data.msg));
        onClose();
      }
    } catch {
      /* network / server error */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dir={dir}
      maxWidth="sm"
      fullWidth
      aria-labelledby="consultations-admin-comments-title"
      PaperProps={glassDialogPaperProps()}
      BackdropProps={glassDialogBackdropProps()}
    >
      <DialogTitle
        id="consultations-admin-comments-title"
        sx={{
          fontWeight: 700,
          fontSize: "1.2rem",
          lineHeight: 1.35,
          letterSpacing: dir === "rtl" ? 0 : undefined,
          pb: 1.25,
          pt: 3,
          px: { xs: 2.5, sm: 3.5 },
          color: "rgba(49, 46, 129, 0.96)",
          textAlign: "center",
        }}
      >
        {t("Admin comments")}
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2.5, sm: 3.5 },
          pt: 0.5,
          pb: 2,
        }}
      >
        <TextField
          value={value}
          onChange={(e) => setValue(e.target.value)}
          multiline
          minRows={9}
          maxRows={20}
          fullWidth
          variant="outlined"
          placeholder={t("Admin comments placeholder")}
          inputProps={{
            dir,
            style: { textAlign: dir === "rtl" ? "right" : "left" },
          }}
          InputProps={{
            sx: {
              borderRadius: "12px",
              fontSize: "0.9375rem",
              lineHeight: 1.55,
              alignItems: "flex-start",
              bgcolor: "rgba(255, 255, 255, 0.38)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              py: 0.5,
              "& textarea": {
                padding: "14px 16px",
                boxSizing: "border-box",
              },
              "& .MuiOutlinedInput-input::placeholder": {
                color: "rgba(100, 116, 139, 0.8)",
                opacity: 1,
              },
              "& fieldset": {
                borderColor: "rgba(255, 255, 255, 0.5)",
              },
              "&:hover fieldset": {
                borderColor: "rgba(99, 102, 241, 0.42)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "rgba(79, 70, 229, 0.55)",
                borderWidth: "1px",
                boxShadow: "0 0 0 3px rgba(79, 70, 229, 0.12)",
              },
            },
          }}
        />
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3.5 },
          pb: 3,
          pt: 0,
          justifyContent: "center",
          mt: -0.5,
        }}
      >
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !meetingId}
          sx={{
            minWidth: 200,
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
            py: 1.15,
            px: 4,
            border: "1px solid rgba(255, 255, 255, 0.35)",
            boxShadow:
              "0 10px 28px rgba(67, 56, 202, 0.28), inset 0 1px 0 rgba(255,255,255,0.22)",
            bgcolor: "rgba(67, 56, 202, 0.88)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            color: "rgba(255,255,255,0.98)",
            "&:hover": {
              bgcolor: "rgba(55, 48, 163, 0.92)",
              boxShadow:
                "0 12px 32px rgba(67, 56, 202, 0.34), inset 0 1px 0 rgba(255,255,255,0.28)",
            },
            "&.Mui-disabled": {
              bgcolor: "rgba(148, 163, 184, 0.4)",
              borderColor: "rgba(255,255,255,0.2)",
              color: "rgba(255,255,255,0.85)",
            },
          }}
        >
          {t("Submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
